import { useMemo } from "react";
import { AlignLeft, AlignCenter, AlignRight, AlignJustify } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SelectGroup,
    SelectLabel,
} from "@/components/ui/select";
import { NumberInput } from "@/components/ui/input-number";
import { ColorPicker } from "@/components/ui/color-picker";
import { TailwindColorSelectorPopover } from "@/components/editor/themes/components/tailwind-color-selector-popover";

import { FONT_FAMILY_GROUPS } from "@/constants/property-panel.constants";
import { parseNumber } from "@/lib/visual-editor/helper";
import PropertySection from "../PropertySection";
import { cn } from "@/lib/utils";
import {
    fontFamilyOptions,
    fontSizeOptions,
    fontWeightOptions,
} from "@/lib/visual-editor/helper";

interface TypographySectionProps {
    styles: Record<string, string>;
    onStyleChange: (property: string, value: string) => void;
    onLetterSpacingChange: (value: number | undefined) => void;
    onLineHeightChange: (value: number | undefined) => void;
}

export const TypographySection = ({
    styles,
    onStyleChange,
    onLetterSpacingChange,
    onLineHeightChange,
}: TypographySectionProps) => {
    const alignmentButtons = useMemo(
        () => [
            { value: "left", icon: AlignLeft, matches: ["left", "start"] },
            { value: "center", icon: AlignCenter, matches: ["center"] },
            { value: "right", icon: AlignRight, matches: ["right"] },
            { value: "justify", icon: AlignJustify, matches: ["justify"] },
        ],
        []
    );

    return (
        <PropertySection title="Typography">
            <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Alignment
                    </Label>
                    <div className="flex gap-1">
                        {alignmentButtons.map(
                            ({ value, icon: Icon, matches }) => (
                                <button
                                    key={value}
                                    onClick={() =>
                                        onStyleChange("textAlign", value)
                                    }
                                    className={cn(
                                        "p-1.5 rounded-md transition-colors cursor-pointer",
                                        matches.includes(styles.textAlign)
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "bg-muted text-muted-foreground hover:text-foreground hover:bg-accent"
                                    )}>
                                    <Icon className="w-3.5 h-3.5" />
                                </button>
                            )
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Text Color
                    </Label>
                    <div className="flex items-center gap-2">
                        <ColorPicker
                            value={styles.color}
                            onChange={(value) => onStyleChange("color", value)}
                            className="flex-shrink-0 h-8 w-8 border-border"
                        />
                        <TailwindColorSelectorPopover
                            value={styles.color}
                            onSelect={(color) => onStyleChange("color", color)}
                        />
                        <Input
                            value={styles.color}
                            onChange={(e) =>
                                onStyleChange("color", e.target.value)
                            }
                            className="h-8 bg-background border-border text-foreground text-xs font-mono"
                            placeholder="#000000"
                        />
                    </div>
                </div>
            </div>

            {/* Font Size & Family */}
            <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Font Size
                    </Label>
                    <Select
                        onValueChange={(value) =>
                            onStyleChange("fontSize", value)
                        }
                        value={styles.fontSize}>
                        <SelectTrigger className="w-full bg-background border border-border rounded-md text-foreground h-8 text-xs">
                            <SelectValue placeholder="Select a font size" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel className="text-xs">
                                    Font Size
                                </SelectLabel>
                                {fontSizeOptions.map((size) => (
                                    <SelectItem
                                        className="text-xs"
                                        key={size.value}
                                        value={size.value}>
                                        {size.title.toUpperCase()}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Font Family
                    </Label>
                    <Select
                        onValueChange={(value) =>
                            onStyleChange("fontFamily", value === "none" ? "inherit" : value)
                        }
                        value={styles.fontFamily}>
                        <SelectTrigger className="w-full bg-background border border-border rounded-md text-foreground h-8 text-xs">
                            <SelectValue placeholder="Select a font family" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem className="text-xs" value="none">
                                None (inherited)
                            </SelectItem>
                            {FONT_FAMILY_GROUPS.map(({ label, start, end }) => (
                                <SelectGroup key={label}>
                                    <SelectLabel className="text-xs">
                                        {label}
                                    </SelectLabel>
                                    {fontFamilyOptions
                                        .slice(
                                            start,
                                            end === -1 ? undefined : end
                                        )
                                        .map((font) => (
                                            <SelectItem
                                                className="text-xs"
                                                key={font.value}
                                                value={font.value}
                                                style={{
                                                    fontFamily: font.value,
                                                }}>
                                                {font.title}
                                            </SelectItem>
                                        ))}
                                </SelectGroup>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Font Weight, Letter Spacing, Line Height */}
            <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Font Weight
                    </Label>
                    <Select
                        onValueChange={(value) =>
                            onStyleChange("fontWeight", value)
                        }
                        value={styles.fontWeight}>
                        <SelectTrigger className="w-full bg-background border border-border rounded-md text-xs h-8 text-foreground">
                            <SelectValue placeholder="Select a font weight" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Font Weight</SelectLabel>
                                {fontWeightOptions.map((weight) => (
                                    <SelectItem
                                        className="text-xs"
                                        key={weight.value}
                                        value={weight.value}>
                                        {weight.title}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Letter Spacing
                    </Label>
                    <NumberInput
                        value={parseNumber(styles.letterSpacing)}
                        onValueChange={onLetterSpacingChange}
                        placeholder="0"
                        suffix="px"
                        decimalScale={0}
                        fixedDecimalScale
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Line Height
                    </Label>
                    <NumberInput
                        value={parseNumber(styles.lineHeight)}
                        onValueChange={onLineHeightChange}
                        placeholder="1.5"
                        decimalScale={1}
                        fixedDecimalScale
                    />
                </div>
            </div>
        </PropertySection>
    );
};
